/**
 * React Query hook for listing reusable PageLayout template families in a deck.
 *
 * The catalog deliberately returns LayoutTemplate summaries, not header_tree bodies.
 * New-page setup uses this hook to select and reuse an existing family; custom
 * cloning/editing needs a separate full-tree workflow.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { LayoutTemplateCatalogPage } from '@services/api/deckManagement/graphql/schema/API';
import { getColumnTemplateCatalog } from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig.ts';

export interface UseGetColumnTemplateCatalogParams {
  deckId?: string;
  layoutFamily?: string;
  deckCycle?: string;
  grain?: string;
  limit?: number;
  nextToken?: string;
  options?: StandardQueryOptions;
}

export const columnTemplateCatalogQueryKeys = {
  all: createQueryOrMutationKey('columnTemplateCatalog'),
  catalog: (deckId: string, layoutFamily: string, deckCycle: string, grain: string, limit: number, nextToken: string) =>
    createQueryOrMutationKey(
      ...columnTemplateCatalogQueryKeys.all,
      deckId,
      layoutFamily,
      deckCycle,
      grain,
      String(limit),
      nextToken,
    ),
};

/**
 * Retrieves one page of reusable LayoutTemplate summaries for a deck.
 */
export const useGetColumnTemplateCatalog = ({
  deckId,
  layoutFamily,
  deckCycle,
  grain,
  limit,
  nextToken,
  options,
}: UseGetColumnTemplateCatalogParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: columnTemplateCatalogQueryKeys.catalog(
      deckId || '',
      layoutFamily || '',
      deckCycle || '',
      grain || '',
      limit || 0,
      nextToken || '',
    ),
    queryFn: async (): Promise<LayoutTemplateCatalogPage> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      const response = await deckManagementClient.graphql({
        query: getColumnTemplateCatalog,
        variables: {
          deck_id: deckId,
          layout_family: layoutFamily,
          deck_cycle: deckCycle,
          grain,
          limit,
          nextToken,
        },
      });

      if ('data' in response && response.data?.getColumnTemplateCatalog) {
        return response.data.getColumnTemplateCatalog as LayoutTemplateCatalogPage;
      }

      throw new Error('Failed to fetch column template catalog');
    },
    ...createStandardQueryOptions<LayoutTemplateCatalogPage>({
      ...options,
      enabled: Boolean(deckId) && (options?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getColumnTemplateCatalog),
    }),
  });
};
